import "./rapportini.scss";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { confirm } from "devextreme/ui/dialog";
import { Button, DataGrid, DateBox, DropDownButton, LoadPanel, SelectBox, SpeedDialAction, Toast } from "devextreme-react";
import { Column, Toolbar, DataGridRef, Sorting, Selection, DataGridTypes, SearchPanel, Item } from "devextreme-react/data-grid";
import { useUserSessionContext } from "../../../../contexts/UserSessionContext";
import { useScreenSize, useScreenSizeClass } from "../../../../utils/media-query";
import config from "devextreme/core/config";

import { ATS_RapportoLavoroPopupCreate, ATS_RapportoLavoroPopupEdit } from "../../components";
import { PopupCreateHandle as LavoroPopupCreateHandle } from "../../components/popup-rapporto-lavoro/popupCreate";
import { PopupEditHandle as LavoroPopupEditHandle } from "../../components/popup-rapporto-lavoro/popupEdit";

import { ATS_RapportoInterventoPopupCreate, ATS_RapportoInterventoPopupEdit } from "../../components";
import { PopupCreateHandle as InterventoPopupCreateHandle } from "../../components/popup-rapporto-intervento/popupCreate";
import { PopupEditHandle as InterventoPopupEditHandle } from "../../components/popup-rapporto-intervento/popupEdit";

import { ATS_RapportoPopupReport, ATS_RapportoPopupInvioReport } from "../../components";
import { PopupInvioReportHandle } from "../../components/popup-rapporto-report/popupInvioReport";
import { PopupReportHandle } from "../../components/popup-rapporto-report/popupReport";

import { useRapportinoDataSource } from '../../../../datasources/rapportinoDataSource';
import useApiConfiguration from "../../../../apiConfig";
import { PersonaleApi, RapportiniApi } from "../../../../data";

export default function Rapportini() {
    const gridRef = useRef<DataGridRef>(null);

    const lavoroPopupCreateRef = useRef<LavoroPopupCreateHandle>(null);
    const lavoroPopupEditRef = useRef<LavoroPopupEditHandle>(null);

    const interventoPopupCreateRef = useRef<InterventoPopupCreateHandle>(null);
    const interventoPopupEditRef = useRef<InterventoPopupEditHandle>(null);

    const popupInvioReportRef = useRef<PopupInvioReportHandle>(null);
    const popupReportRef = useRef<PopupReportHandle>(null);

    const apiConfiguration = useApiConfiguration();
    const { user } = useUserSessionContext();
    const { isLarge, isMedium } = useScreenSize();
    const screenSizeClass = useScreenSizeClass();

    const [loading, setLoading] = useState(false);
    const [dataDa, setDateDa] = useState(() => {
        const today = new Date();
        const prevWeek = new Date(today);
        prevWeek.setDate(today.getDate() - 30);
        return prevWeek;
    });
    const [dataAl, setDateAl] = useState(new Date());
    const [personaleId, setPersonaleId] = useState(user.id);
    const [dataSourcePersonale, setDataSourcePersonale] = useState<any[]>([]);
    const [canChangePersonale, setChangePersonale] = useState(false);
    const [selectedRapportino, setSelectedRapportino] = useState(-1);
    const [selectedTipoRapportino, setSelectedTipoRapportino] = useState("");
    const [selectedCanEdit, setSelectedCanEdit] = useState<boolean>(false);
    const [selectedFirmato, setSelectedFirmato] = useState<boolean>(false);
    const [selectedCliente, setSelectedCliente] = useState<string>('');

    const [toastConfig, setToastConfig] = useState({ isVisible: false, message: '' } as { isVisible: boolean, message: string });

    const rapportinoDataSource = useRapportinoDataSource(personaleId, dataDa, dataAl);

    config({
        floatingActionButtonConfig: {
            icon: "optionsgear",
            shading: true,
            position: {
                my: "right bottom",
                at: "right bottom",
                of: "#gridLavori",
                offset: '-16 -16'
            }
        }
    });

    function handleGridRowClick(e: DataGridTypes.RowClickEvent) {
        e.event.preventDefault();
        e.event.stopPropagation();

        if (isLarge || isMedium)
            return;

        const idRapportino = e.data?.id;
        if (idRapportino == null) return;

        if (idRapportino == selectedRapportino) {
            setSelectedRapportino(-1);
            setSelectedTipoRapportino("");
            setSelectedCanEdit(false);
            setSelectedFirmato(false);
            setSelectedCliente('');
            gridRef.current.instance().deselectAll();
        } else {
            setSelectedRapportino(idRapportino);
            setSelectedTipoRapportino(e.data?.tipoRapportino);
            setSelectedCanEdit(e.data?.dataFirma == null || user.isAdmin);
            setSelectedFirmato(e.data?.dataFirma != null);
            setSelectedCliente(e.data?.codiceCliente);
        }
    };

    function changeDipendente(e: any) {
        setPersonaleId(e.value);
    };

    function canDeleteOrModify(cellData: any): boolean {
        if (cellData.data && ((cellData.data.dataFirma == null) || user.isAdmin))
            return true;

        return false;
    };

    function newRapportino(tipologia: string) {
        if (tipologia === "L")
            lavoroPopupCreateRef.current?.show(tipologia);
        else
            interventoPopupCreateRef.current?.show(tipologia);
    };

    function editRapportino(id: number, tipologia: string) {
        if (tipologia === "L")
            lavoroPopupEditRef.current?.show(id);
        else
            interventoPopupEditRef.current?.show(id);
    };

    function checkDelete(id: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes)
                deleteLavoro(id);
        })
    };

    function deleteLavoro(id: number) {
        var api = new RapportiniApi(apiConfiguration);

        api.deleteRapportinoById({ id: id })
            .then(() => {
                refresh();
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    message: 'Cancellazione completata'
                });
            });
    };

    const handleOpenFileRapportino = async (id: number, isFirmato: boolean) => {
        popupReportRef.current?.show(id, isFirmato);
    };

    function inviaFileRapportino(id: number, codiceCliente: string) {
        popupInvioReportRef.current?.show(id, codiceCliente);
    };

    const refresh = useCallback(() => {
        gridRef.current?.instance().refresh();
    }, []);

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    useEffect(() => {
        const api = new PersonaleApi(apiConfiguration);
        api.getPersonale({ date: dataAl }).then((res) => {
            setDataSourcePersonale(res);
            setChangePersonale(res.length > 1);
        });
    }, [dataAl]);

    useEffect(() => {
        setSelectedRapportino(-1);
        gridRef.current?.instance().deselectAll();
    }, [isLarge, isMedium]);

    return (
        <React.Fragment>
            {
                loading && <LoadPanel style={{ zIndex: 9999 }} shading={true} shadingColor="rgba(0, 0, 0, 0.4)" position={'center'} visible={true} />
            }

            <div className="view-list-rapportini">
                <div className={`view-wrapper view-wrapper-list list-page ${screenSizeClass}`}>
                    <DataGrid
                        id="gridRapportini" ref={gridRef} key="id"
                        dataSource={rapportinoDataSource}
                        showBorders={true} rowAlternationEnabled={true}
                        noDataText="Nessun rapportino caricato"
                        className="grid theme-dependent"
                        onRowClick={handleGridRowClick}
                    >
                        <Sorting mode="single" />
                        <SearchPanel visible placeholder="Ricerca" />

                        {!isLarge && !isMedium && <Selection mode="single" />}

                        <Toolbar>
                            <Item location="before">
                                <div className="grid-header">Rapportini</div>
                            </Item>

                            <Item
                                location="center"
                                locateInMenu="auto"
                                showText="inMenu"
                                widget="dxSelectBox"
                                visible={canChangePersonale}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <SelectBox className="dx-dipendente"
                                        label="Dipendente" labelMode="floating"
                                        dataSource={dataSourcePersonale}
                                        displayExpr="descrizione"
                                        valueExpr="idPersonale"
                                        searchEnabled={true}
                                        value={personaleId}
                                        onValueChanged={changeDipendente}
                                    />
                                </div>
                            </Item>

                            <Item
                                location="center"
                                locateInMenu={ isLarge || isMedium ? "never" : "auto"}
                                showText="inMenu"
                                widget="dxDateBox"
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexDirection: (isLarge || isMedium ? "row" : "column") }}>
                                    <DateBox className={`dataSearch ${screenSizeClass}`}
                                        label="Data Da"
                                        labelMode="floating"
                                        value={dataDa}
                                        onValueChanged={(e) => setDateDa(e.value)}
                                        type="date"
                                        displayFormat={isLarge ? "EEEE d MMMM yyyy" : "dd/MM/yyyy"}
                                        inputAttr={{ readonly: true }}
                                        openOnFieldClick={true}
                                    />

                                    <DateBox className={`dataSearch ${screenSizeClass}`}
                                        label="Data A"
                                        labelMode="floating"
                                        value={dataAl}
                                        onValueChanged={(e) => setDateAl(e.value)}
                                        type="date"
                                        displayFormat={isLarge ? "EEEE d MMMM yyyy" : "dd/MM/yyyy"}
                                        inputAttr={{ readonly: true }}
                                        openOnFieldClick={true}
                                    />
                                </div>
                            </Item>

                            <Item
                                location="after"
                                locateInMenu="auto"
                                showText="inMenu"
                                widget="dxDropDownButton"
                            >
                                <DropDownButton icon="add" stylingMode="text" visible={isLarge || isMedium}
                                    dropDownOptions={{ width: 230 }} showArrowIcon={false}
                                    items={[
                                        { id: 'L', text: 'Rapporto M. 25' },
                                        { id: 'I', text: 'Rapporto M. 111' }
                                    ]}
                                    displayExpr="text"
                                    keyExpr="id"
                                    onItemClick={(e) => newRapportino(e.itemData.id)}
                                />
                            </Item>

                            <Item
                                location="after"
                                locateInMenu="auto"
                                showText="inMenu"
                                widget="dxButton"
                            >
                                <Button
                                    icon="refresh"
                                    text="Refresh"
                                    stylingMode="text"
                                    onClick={refresh}
                                />
                            </Item>

                            <Item 
                                location="after"
                                locateInMenu="auto"
                                showText="inMenu"
                                name="searchPanel"
                            />
                        </Toolbar>

                        <Column
                            visible={isLarge || isMedium}
                            width={140}
                            cellRender={(cellData) => (
                                <div style={{ display: "flex" }}>
                                    <Button hint="Vedi Rapportino" stylingMode="text" onClick={() => handleOpenFileRapportino(cellData.data.id, (cellData.data.dataFirma != null))}>
                                        <i className='dx-icon material-symbols-outlined'>docs</i>
                                    </Button>
                                    <Button hint="Invia Rapportino" stylingMode="text" visible={cellData.data.dataFirma != null} onClick={() => inviaFileRapportino(cellData.data.id, cellData.data.codiceCliente)}>
                                        <i className='dx-icon material-symbols-outlined'>forward</i>
                                    </Button>
                                    <Button icon="edit" hint="Modifica" stylingMode="text" visible={canDeleteOrModify(cellData)} onClick={() => editRapportino(cellData.data.id, cellData.data.tipoRapportino)} />
                                    <Button icon="trash" hint="Elimina" stylingMode="text" visible={canDeleteOrModify(cellData)} onClick={() => checkDelete(cellData.data.id)} />
                                </div>
                            )}
                        />
                        <Column dataField="id" visible={false} />
                        <Column dataField="dataRapportino" caption="Data" width={120} />
                        <Column caption="Tipologia" width="140" cellRender={({ data }) => data.tipoRapportino === "L" ? "Rapporto M. 25" : "Rapporto M. 111"} />
                        <Column dataField="cliente" hidingPriority={1} />
                        <Column dataField="commessa" hidingPriority={0} />
                    </DataGrid>

                    <SpeedDialAction
                        icon="add" label="Rapporto M. 25"
                        visible={!isLarge && !isMedium && (selectedRapportino < 0)}
                        index={1} onClick={() => newRapportino('L')}
                    />
                    <SpeedDialAction
                        icon="add" label="Rapporto M. 111"
                        visible={!isLarge && !isMedium && (selectedRapportino < 0)}
                        index={2} onClick={() => newRapportino('I')}
                    />
                    <SpeedDialAction
                        icon="edit" label="Modifica"
                        visible={!isLarge && !isMedium && selectedRapportino > 0 && selectedCanEdit}
                        index={1} onClick={() => editRapportino(selectedRapportino, selectedTipoRapportino)}
                    />
                    <SpeedDialAction
                        icon="trash" label="Elimina"
                        visible={!isLarge && !isMedium && selectedRapportino > 0 && selectedCanEdit}
                        index={2} onClick={() => checkDelete(selectedRapportino)}
                    />
                    <SpeedDialAction 
                        icon="custom-docs material-symbols-outlined" label="Vedi Rapportino"
                        visible={!isLarge && !isMedium && selectedRapportino > 0}
                        index={3} onClick={() => handleOpenFileRapportino(selectedRapportino, selectedFirmato)}
                    />
                    <SpeedDialAction 
                        icon="custom-mail material-symbols-outlined" label="Invia Rapportino"
                        visible={!isLarge && !isMedium && selectedRapportino > 0 && selectedFirmato}
                        index={4} onClick={() => inviaFileRapportino(selectedRapportino, selectedCliente)}
                    />
                </div>
            </div>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                type="success"
                onHiding={onHidingToast} closeOnClick={true}
                displayTime={3000}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 40, to: 0 } }}
            />

            <ATS_RapportoLavoroPopupCreate ref={lavoroPopupCreateRef} onChange={refresh} />
            <ATS_RapportoLavoroPopupEdit ref={lavoroPopupEditRef} onChange={refresh} />

            <ATS_RapportoInterventoPopupCreate ref={interventoPopupCreateRef} onChange={refresh} />
            <ATS_RapportoInterventoPopupEdit ref={interventoPopupEditRef} onChange={refresh} />

            <ATS_RapportoPopupInvioReport ref={popupInvioReportRef} />
            <ATS_RapportoPopupReport ref={popupReportRef} onHide={refresh} />
        </React.Fragment>
    )
}